import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Observable, catchError, tap, Subject, BehaviorSubject, throwError, share } from 'rxjs';
import { AppService } from '../app-service/app.service';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';

import { environment } from '../../../environments/environment';
import { FormGroup } from '@angular/forms';
import { ApiResponse } from '../../models/commonModel';
import { ErrorMessageConstants, TokenConstants } from '../../constants/MessageConstants';
import { isPermissionDenied } from '../../models/permission.model';
@Injectable({
  providedIn: 'root'
})

export class CommonService {

  public emailRegex: RegExp = /^(?=.{1,30}@)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  private readonly userImageSource = new BehaviorSubject<string | null>(null);
  private readonly userNameSource = new BehaviorSubject<string | null>(null);

  userName$ = this.userNameSource.asObservable();
  userImage$ = this.userImageSource.asObservable();

  setAutoHide: boolean = true;
  autoHide: number = 2000;
  private logoutSubject = new Subject<void>();
  public logoutAction$ = this.logoutSubject.asObservable();
  public ActiveModelformGroup: FormGroup | undefined;
  public href: any;
  IsError: boolean = false;
  isSystemAlert: boolean = false;
  private readonly csrfCookieName = 'collectx_admin_xsrf';
  private readonly csrfHeaderName = 'X-CSRF-TOKEN';

  constructor(
    private readonly toster: ToastrService,
    private readonly dialog: MatDialog,
    private readonly spinner: NgxSpinnerService,
    private readonly appService: AppService,
    private readonly http: HttpClient
  ) { }

  doGet(apiUrl: String): Observable<ApiResponse> {
    const httpOptions = {
      headers: new HttpHeaders(),
      withCredentials: true
    };
    const url = `${environment.BaseURL}${apiUrl}`;
    return this.http.get<ApiResponse>(url, httpOptions).pipe(
      tap(() => this.log(`doGet success`)),
      catchError((error: HttpErrorResponse) => {
        this.checkAuthorize(error);
        return throwError(() => error);
      })
    );
  }

  doPost(apiUrl: string, postData: any, extraHeaders?: Record<string, string>): Observable<ApiResponse> {
    let headers = this.addCsrfHeader(new HttpHeaders());
    if (extraHeaders) {
      Object.entries(extraHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }
    const httpOptions = { headers, withCredentials: true };
    const url = `${environment.BaseURL}${apiUrl}`;
    return this.http.post<ApiResponse>(url, postData, httpOptions).pipe(
      tap(() => this.log(`doPost success`)),
      catchError((error: HttpErrorResponse) => {
        this.checkAuthorize(error);
        return throwError(() => error);
      })
    );
  }

  doPut(apiUrl: string, putData: any): Observable<ApiResponse> {
    const httpOptions = {
      headers: this.addCsrfHeader(new HttpHeaders()),
      withCredentials: true
    };
    const url = `${environment.BaseURL}${apiUrl}`;
    return this.http.put<ApiResponse>(url, putData, httpOptions).pipe(
      tap(() => this.log(`doGet success`)),
      catchError((error: HttpErrorResponse) => {
        this.checkAuthorize(error);
        return throwError(() => error);
      })
    );
  }

  doDelete(apiUrl: String, _idtoDelete: any): Observable<ApiResponse> {
    const httpOptions = {
      headers: this.addCsrfHeader(new HttpHeaders()),
      withCredentials: true
    };

    const url = (`${environment.BaseURL}${apiUrl}`);
    return this.http.delete<ApiResponse>(url, httpOptions).pipe(
      tap(() => this.log(`doGet success`)),
      catchError((error: HttpErrorResponse) => {
        this.checkAuthorize(error);
        return throwError(() => error);
      })
    );
  }

  downloadFilePost(apiUrl: string, postData: any): Observable<Blob> {
    const url = `${environment.BaseURL}${apiUrl}`;
    const headers = this.addCsrfHeader(new HttpHeaders());

    return this.http.post<Blob>(url, postData,
      {
        responseType: 'blob' as 'json',
        headers: headers,
        withCredentials: true
      }).pipe(
        share()
      );
  }

  downloadFileGet(apiUrl: string): Observable<Blob> {
    const url = `${environment.BaseURL}${apiUrl}`;

    return this.http.get<Blob>(url,
      {
        responseType: 'blob' as 'json',
        withCredentials: true
      }).pipe(
        share()
      );
  }

  checkAuthorize(error: any) {
    // Permission denials are toasted by AuthInterceptor and must not end the session.
    if (isPermissionDenied(error)) {
      this.spinner.hide();
      return;
    }

    if (error.status == HttpStatusCode.Unauthorized) {
      if (this.IsError == false) {
        this.IsError = true
        this.toster.error(TokenConstants.Session_Expired);
      }
      this.spinner.hide();
      this.appService.logout();
      this.dialog.closeAll();
    }
    else if (error.status == HttpStatusCode.Forbidden) {
      if (this.IsError == false) {
        this.IsError = true
        this.toster.error(TokenConstants.Session_Expired);
        localStorage.clear();
        this.clearCsrfToken();
      }
      this.appService.logout();
      this.spinner.hide();
      this.dialog.closeAll();
    }
    else if (error.status === HttpStatusCode.InternalServerError) {
      this.toster.error(ErrorMessageConstants.Message)
      this.appService.logout();
      this.spinner.hide();
      this.dialog.closeAll();
    }
  }

  private log(_message: string) {
    this.IsError = false;
  }

  setUserName(name: string | null) {
    this.userNameSource.next(name);
  }

  getUserName(): Observable<string | null> {
    return this.userName$;
  }

  setUserImage(imageSrc: string | null) {
    this.userImageSource.next(imageSrc);
  }

  getUserImage(): Observable<string | null> {
    return this.userImage$;
  }

  private formSubmittedSubject = new BehaviorSubject<boolean>(false);
  formSubmitted$ = this.formSubmittedSubject.asObservable();

  setFormSubmitted(value: boolean): void {
    this.formSubmittedSubject.next(value);
  }

  getFormSubmitted(): boolean {
    return this.formSubmittedSubject.value;
  }

  Encrypt(input: string): string {
    try {
      return btoa(input);
    } catch (e) {
      console.error('Encoding failed', e);
      return '';
    }
  }
  Decrypt(encodedInput: string): string {
    try {
      return atob(encodedInput);
    } catch (e) {
      console.error('Decoding failed', e);
      return '';
    }
  }

  private addCsrfHeader(headers: HttpHeaders): HttpHeaders {
    const csrfToken = this.getCsrfToken();
    if (!csrfToken) {
      return headers;
    }
    return headers.set(this.csrfHeaderName, csrfToken);
  }

  /** Prefer readable cookie; fall back to sessionStorage (cross-subdomain production). */
  private getCsrfToken(): string {
    return this.getCookieValue(this.csrfCookieName) || sessionStorage.getItem(this.csrfCookieName) || '';
  }

  private getCookieValue(name: string): string {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  setCsrfToken(token: string | null | undefined): void {
    if (!token) {
      sessionStorage.removeItem(this.csrfCookieName);
      return;
    }
    sessionStorage.setItem(this.csrfCookieName, token);
  }

  clearCsrfToken(): void {
    sessionStorage.removeItem(this.csrfCookieName);
  }
}